import { gql } from '@apollo/client'

// Keep these selections inline. This application intentionally runs Apollo with
// addTypename disabled, so fragment-only selections can be discarded by cache
// normalization (see saved-filter-views.query.js).
// Cadence step graph v2 (SPT-2164 §4). A nested split holds branches whose
// steps are leaf steps (one nesting level), so the selection is two levels deep.
const STEP_FIELDS = `
	id key kind position delayAmount delayUnit sendTiming sendTimeOfDay alertRecruiter rejoin unreachable
	subject cadenceEmailTemplateId cadenceEmailTemplateVersion fallbackTemplateId sentCount
	cadenceEmailTemplate { id key name subject effectiveSenderName effectiveSenderEmail }
`
const BRANCH_FIELDS = `id key name description position rule ruleDescription steps { ${STEP_FIELDS} }`
const PATH_FIELDS = `id key name description position rule ruleDescription enrolledCount steps { ${STEP_FIELDS} branches { ${BRANCH_FIELDS} } }`
const CADENCE_FIELDS = `
	id key name description category status settings audienceRule exitRule publishedAt updatedAt sendable
	audienceKey audienceLabel recheckBeforeSend enrolledCount midCadenceCount emailCount pathCount sourceCadenceId
	editMode canReturnToDraft rollout rolloutDescription rolloutNamedWorkers { id name email removed }
	validation { publishable issues { code severity message pathKey stepKey branchKey } }
	paths { ${PATH_FIELDS} }
`

export const CADENCE_CAPABILITIES_QUERY = gql`
	query cadenceCapabilities {
		cadenceCapabilities {
			read
			edit
			publish
			deliveryEnabled
		}
	}
`

export const CADENCES_QUERY = gql`
	query cadences {
		cadences {
			id
			key
			name
			description
			category
			status
			updatedAt
			audienceKey
			audienceLabel
			rollout
			rolloutDescription
			pathCount
			emailCount
		}
	}
`

// Separate from CADENCE_CAPABILITIES_QUERY on purpose: auth.store gates the
// Configuration menu on that query, and a pre-v2 API rejects these fields.
export const CADENCE_GRAPH_CAPABILITIES_QUERY = gql`
	query cadenceGraphCapabilities {
		cadenceCapabilities {
			graphVersion
			stepKinds
			delayUnits
			sendTimings
			alertRecruiterSupported
		}
	}
`

export const CADENCE_PRESETS_QUERY = gql`
	query cadencePresets {
		cadencePresets {
			key
			name
			description
		}
		cadenceAudiences {
			key
			label
			description
			audienceRule
		}
	}
`

// Configuration hub card meta only: counts, never paths/steps or template bodies.
export const CONFIGURATION_HUB_COUNTS_QUERY = gql`
	query configurationHubCounts {
		cadences {
			id
			status
		}
		cadenceEmailTemplates {
			id
		}
	}
`

export const CADENCE_QUERY = gql`
	query cadence($id: ID!) { cadence(id: $id) { ${CADENCE_FIELDS} } }
`

// Candidates tab (SPT-2164 phase 2). Rows are the same shape in the list and the drawer; `state` is derived
// server-side (HELD = running but the last tick could not act), never stored.
const ENROLLMENT_ROW_FIELDS = `
	id state exitReason heldReason enrolledAt nextRunAt lastSentAt lastEventAt sentCount holdout
	worker { id firstName lastName fullName email profileCompletionPercent }
	path { id key name }
	currentStep { id key kind delayAmount delayUnit cadenceEmailTemplate { id name subject } }
`

// Tab strip badge and filter counts, plus the paths for the path filter. Separate from CADENCE_QUERY so the
// Candidates tab never loads the whole step graph.
export const CADENCE_CANDIDATES_SUMMARY_QUERY = gql`
	query cadenceCandidatesSummary($id: ID!) {
		cadence(id: $id) {
			id
			name
			status
			enrollmentSummary {
				live
				held
				completed
				exited
				paused
				total
			}
			paths {
				id
				key
				name
			}
		}
	}
`

export const CADENCE_ENROLLMENTS_QUERY = gql`
	query cadenceEnrollments($cadenceId: ID!, $state: [CadenceEnrollmentState!], $pathId: ID, $search: String, $first: Int, $after: String) {
		cadenceEnrollments(cadenceId: $cadenceId, state: $state, pathId: $pathId, search: $search, first: $first, after: $after) {
			totalCount
			pageInfo { hasNextPage endCursor }
			nodes { ${ENROLLMENT_ROW_FIELDS} }
		}
	}
`

export const CADENCE_ENROLLMENT_QUERY = gql`
	query cadenceEnrollment($id: ID!) {
		cadenceEnrollment(id: $id) {
			${ENROLLMENT_ROW_FIELDS}
			events {
				id eventType reason messageId detail occurredAt
				step { id key kind cadenceEmailTemplate { id name subject } }
				path { id key name }
			}
		}
	}
`

// Smart communication S2: a workflow's Performance tab.
export const CADENCE_STATS_QUERY = gql`
	query cadenceStats($id: ID!, $from: ISO8601Date!, $to: ISO8601Date!, $channels: [String!], $kinds: [String!], $pathKeys: [String!]) {
		cadenceStats(id: $id, from: $from, to: $to, channels: $channels, kinds: $kinds, pathKeys: $pathKeys) {
			from
			to
			updatedAt
			goal {
				type
				windowDays
			}
			totals {
				enrolled
				sent
				failed
				skipped
				heldOut
				delivered
				opened
				clicked
				jobClicks
				botClicks
				bounced
				unsubscribed
				goalMet
			}
			conversion {
				enrolled
				goalMet
				rate
				holdoutEnrolled
				holdoutGoalMet
				holdoutRate
				lift
			}
			steps {
				stepId
				stepKey
				stepName
				pathKey
				pathName
				channel
				kind
				sent
				failed
				skipped
				heldOut
				delivered
				opened
				clicked
				jobClicks
				botClicks
				bounced
				goalMet
			}
			days {
				date
				sent
				clicked
				enrolled
				goalMet
			}
		}
	}
`

// Smart communication S4: candidates who acted on a workflow message in the range (opened a job from it, replied to a
// text, reached the goal), newest first. `mine: true` keeps only the signed-in recruiter's own candidates.
export const CADENCE_HOT_CANDIDATES_QUERY = gql`
	query cadenceHotCandidates($from: ISO8601Date!, $to: ISO8601Date!, $cadenceId: ID, $mine: Boolean, $limit: Int) {
		cadenceHotCandidates(from: $from, to: $to, cadenceId: $cadenceId, mine: $mine, limit: $limit) {
			worker {
				id
				firstName
				lastName
				fullName
				email
				profileCompletionPercent
			}
			recruiterName
			signalCount
			jobClicks
			smsReplies
			goalsMet
			lastSignalAt
			lastKind
			lastDetail
			lastCadenceName
		}
	}
`

export const CADENCE_RULE_ATTRIBUTES_QUERY = gql`
	query cadenceRuleAttributes {
		cadenceRuleAttributes {
			key
			label
			description
			type
			operators
			values
		}
	}
`

// `rollout` (SPT-2164 pilot) previews a draft setting in place of the saved one: {} = everyone, omitted = saved.
// Smart communication S3: the audience picker's list plus tenant segments (custom), each with its rule as a sentence and
// the workflows on it. A separate document so the builder's own catalog query works against an older server.
export const CADENCE_SEGMENTS_QUERY = gql`
	query cadenceSegments {
		cadenceAudiences {
			id
			key
			label
			description
			audienceRule
			custom
			archived
			summary
			usedBy {
				id
				name
				status
			}
		}
	}
`

export const SAVE_CADENCE_AUDIENCE_MUTATION = gql`
	mutation saveCadenceAudience($id: ID, $label: String!, $description: String, $audienceRule: JSON!) {
		saveCadenceAudience(id: $id, label: $label, description: $description, audienceRule: $audienceRule) {
			id
			key
			label
			description
			audienceRule
			custom
			archived
			summary
		}
	}
`

export const ARCHIVE_CADENCE_AUDIENCE_MUTATION = gql`
	mutation archiveCadenceAudience($id: ID!) {
		archiveCadenceAudience(id: $id) {
			id
			key
			archived
		}
	}
`

export const CADENCE_AUDIENCE_PREVIEW_QUERY = gql`
	query cadenceAudiencePreview($audienceRule: JSON!, $exitRule: JSON, $rollout: JSON) {
		cadenceAudiencePreview(audienceRule: $audienceRule, exitRule: $exitRule, rollout: $rollout) {
			matchingCount
			enrollableCount
			enrolledCount
			completedCount
			exitedCount
			audienceRuleDescription
			exitRuleDescription
			rolloutDescription
		}
	}
`

export const SAVE_CADENCE_DEFINITION_MUTATION = gql`
	mutation saveCadenceDefinition($cadence: JSON!, $paths: [JSON!]!) {
		saveCadenceDefinition(cadence: $cadence, paths: $paths) { ${CADENCE_FIELDS} }
	}
`

export const SET_CADENCE_STATUS_MUTATION = gql`
	mutation setCadenceStatus($id: ID!, $status: String!) {
		setCadenceStatus(id: $id, status: $status) { ${CADENCE_FIELDS} }
	}
`

export const CREATE_CADENCE_FROM_PRESET_MUTATION = gql`
	mutation createCadenceFromPreset($name: String, $audienceKey: String, $preset: String!) {
		createCadenceFromPreset(name: $name, audienceKey: $audienceKey, preset: $preset) {
			id
			name
		}
	}
`

// Create with AI, deploy-skew probe (spec §7): does the connected schema have planCadenceWithAi? The field is
// @skip(if: true), so a schema that has it validates the document and runs nothing (no AI call, no request used); a
// schema without it rejects the document at validation ("Field 'planCadenceWithAi' doesn't exist", undefinedField).
// Not `__type(...)`: Maestro turns introspection off in production.
export const CADENCE_AI_WORKFLOWS_PROBE_MUTATION = gql`
	mutation cadenceAiWorkflowsProbe {
		planCadenceWithAi(description: "") @skip(if: true) {
			remaining
		}
	}
`

// What a plan request answers, for a new workflow and for a change to a saved one alike.
const AI_PLAN_RESULT_FIELDS = `
	questions { key text options { value label } }
	plan
	notIncluded { asked why }
	assumptions
	issues { code severity message pathKey stepKey branchKey }
	audienceCount
	remaining
`

export const PLAN_CADENCE_WITH_AI_MUTATION = gql`
	mutation planCadenceWithAi($description: String!, $audienceKey: String, $answers: [MaestroCadenceAiAnswerInput!], $plan: JSON, $change: String) {
		planCadenceWithAi(description: $description, audienceKey: $audienceKey, answers: $answers, plan: $plan, change: $change) { ${AI_PLAN_RESULT_FIELDS} }
	}
`

export const CREATE_CADENCE_FROM_AI_PLAN_MUTATION = gql`
	mutation createCadenceFromAiPlan($plan: JSON!) {
		createCadenceFromAiPlan(plan: $plan) {
			id
			name
		}
	}
`

// Change with AI (v2): the saved workflow's plan (toAiPlan) and what should change, for that cadence. A refine also
// sends the panel's earlier requests as `description` (the first request sends none). Sent only after
// CADENCE_AI_CHANGE_PROBE_MUTATION passed: an older schema has neither `cadenceId` nor an optional `description`.
export const PLAN_CADENCE_CHANGE_WITH_AI_MUTATION = gql`
	mutation planCadenceChangeWithAi($cadenceId: ID!, $plan: JSON!, $change: String!, $description: String) {
		planCadenceWithAi(cadenceId: $cadenceId, plan: $plan, change: $change, description: $description) { ${AI_PLAN_RESULT_FIELDS} }
	}
`

// Apply a change: the proposed definition plus the new templates its `ai:<ref>` steps use (aiTemplatesFor). The host
// creates the templates and saves the definition in one transaction.
export const SAVE_CADENCE_DEFINITION_WITH_AI_MUTATION = gql`
	mutation saveCadenceDefinitionWithAi($cadence: JSON!, $paths: [JSON!]!, $aiTemplates: [JSON!]!) {
		saveCadenceDefinition(cadence: $cadence, paths: $paths, aiTemplates: $aiTemplates) { ${CADENCE_FIELDS} }
	}
`

// Change with AI, deploy-skew probe: both new arguments, every field @skip(if: true), so a schema that has them
// validates the document and runs nothing. One without them rejects it at validation ("doesn't accept argument",
// argumentNotAccepted), as a schema without planCadenceWithAi does (undefinedField).
export const CADENCE_AI_CHANGE_PROBE_MUTATION = gql`
	mutation cadenceAiChangeProbe {
		planCadenceWithAi(cadenceId: "0", plan: {}, change: "") @skip(if: true) {
			remaining
		}
		saveCadenceDefinition(cadence: {}, paths: [], aiTemplates: []) @skip(if: true) {
			id
		}
	}
`

export const DUPLICATE_CADENCE_MUTATION = gql`
	mutation duplicateCadence($id: ID!, $name: String) {
		duplicateCadence(id: $id, name: $name) {
			id
			name
		}
	}
`

export const SEND_CADENCE_TEST_EMAIL_MUTATION = gql`
	mutation sendCadenceTestEmail($templateId: ID!, $to: String!, $workerId: ID, $subject: String, $body: String) {
		sendCadenceTestEmail(templateId: $templateId, to: $to, workerId: $workerId, subject: $subject, body: $body) {
			success
			resolvedTags
		}
	}
`

const CADENCE_EMAIL_TEMPLATE_FIELDS = `
	id key name subject body status version usageCount updatedAt
`

export const CADENCE_EMAIL_TEMPLATES_QUERY = gql`
	query cadenceEmailTemplates {
		cadenceEmailTemplates { ${CADENCE_EMAIL_TEMPLATE_FIELDS} }
	}
`

export const CADENCE_EMAIL_TEMPLATE_QUERY = gql`
	query cadenceEmailTemplate($id: ID!) {
		cadenceEmailTemplate(id: $id) { ${CADENCE_EMAIL_TEMPLATE_FIELDS} }
	}
`

export const CADENCE_TEMPLATE_MERGE_TAGS_QUERY = gql`
	query cadenceTemplateMergeTags {
		cadenceTemplateMergeTags
	}
`

export const PREVIEW_CADENCE_EMAIL_TEMPLATE_QUERY = gql`
	query previewCadenceEmailTemplate($id: ID, $subject: String, $body: String) {
		previewCadenceEmailTemplate(id: $id, subject: $subject, body: $body) {
			subject
			body
		}
	}
`

export const SAVE_CADENCE_EMAIL_TEMPLATE_MUTATION = gql`
	mutation saveCadenceEmailTemplate($id: ID, $key: String!, $name: String!, $subject: String!, $body: String!) {
		saveCadenceEmailTemplate(id: $id, key: $key, name: $name, subject: $subject, body: $body) {
			${CADENCE_EMAIL_TEMPLATE_FIELDS}
		}
	}
`

export const ARCHIVE_CADENCE_EMAIL_TEMPLATE_MUTATION = gql`
	mutation archiveCadenceEmailTemplate($id: ID!) {
		archiveCadenceEmailTemplate(id: $id) { ${CADENCE_EMAIL_TEMPLATE_FIELDS} }
	}
`
